import type { StatusTone } from "@/components/ui";
/**
 * Pure status rules for work and system toasts: dot color, label word,
 * attention/terminal grouping, visibility (timed for working/done, sticky
 * for attention/failed/stopped until dismissed), and sort order.
 *
 * No store reads, no timers — callers derive visibility at render time from
 * `now` and mirrored hover/expanded UI state.
 */

import type { SystemToast } from "@/stores/notification-store";
import type { WorkToastItem, WorkToastState } from "@/stores/work-toast-store";
import { isComputerWorkToastPinned } from "@/lib/work-toast-computer-pin";
import { translate } from "@/i18n";
import { toastsMessages } from "@/i18n/messages/toasts";
import type toastsEn from "@/i18n/messages/toasts/en";

export const WORKING_VISIBLE_MS = 3_000;
export const DONE_VISIBLE_MS = 10_000;
export const SYSTEM_INFO_VISIBLE_MS = 8_000;
export const SYSTEM_ACTION_VISIBLE_MS = 15_000;
export const TERMINAL_PRUNE_MS = 60_000;
/** How long settled work keeps its click linked to the app it worked in. */
export const APP_LINK_FRESH_MS = 5 * 60_000;

/** Compact surface standard §7: yellow waits for you, red went wrong, soft
 *  white pulses while working, done shows no dot. */
const DOT: Record<WorkToastState, StatusTone | null> = {
  working: "working",
  question: "question",
  approval: "question",
  blocked: "question",
  done: null,
  failed: "error",
  stopped: "error",
};

const WORD_KEY = {
  working: "stateWorking",
  question: "stateQuestion",
  approval: "stateApproval",
  blocked: "stateBlocked",
  done: "stateDone",
  failed: "stateFailed",
  stopped: "stateStopped",
} as const satisfies Record<WorkToastState, keyof typeof toastsEn>;

export function workToastDot(state: WorkToastState): StatusTone | null {
  return DOT[state];
}
export function workToastWord(state: WorkToastState): string {
  return translate(toastsMessages, WORD_KEY[state]);
}

export function isAttentionState(state: WorkToastState): boolean {
  return state === "question" || state === "approval" || state === "blocked";
}

export function isTerminalState(state: WorkToastState): boolean {
  return state === "done" || state === "failed" || state === "stopped";
}

/** The conversation whose status row an item is. A standalone computer card
 *  knows its conversation for its title and Open chat, but carries no result:
 *  it neither replaces the conversation's finished row nor outlives its fade. */
export function workToastStatusConversation(
  item: WorkToastItem,
): string | null {
  return item.source === "computer" ? null : item.conversationId;
}

/** Derived at render from the item and mirrored UI state; timers never write the store. */
export function isWorkToastVisible(
  item: WorkToastItem,
  now: number,
  ui: { hovered: boolean; expanded: boolean },
): boolean {
  if (item.dismissed) return false;
  if (isComputerWorkToastPinned(item)) return true;
  if (ui.expanded) return true;
  if (isAttentionState(item.state)) return true;
  if (!item.tracked) return false;
  if (item.state === "working")
    return (
      item.computer?.active === true ||
      ui.hovered ||
      now - item.stateChangedAt < WORKING_VISIBLE_MS
    );
  if (item.state === "done") {
    return (
      item.source === "native_worker" ||
      ui.hovered ||
      now - item.stateChangedAt < DONE_VISIBLE_MS
    );
  }
  return true;
}

export function systemToastDot(level: SystemToast["level"]): StatusTone | null {
  if (level === "warning") return "question";
  return level === "error" ? "error" : null;
}

export function compareWorkToasts(a: WorkToastItem, b: WorkToastItem): number {
  const attention =
    Number(isAttentionState(b.state)) - Number(isAttentionState(a.state));
  return attention !== 0 ? attention : b.stateChangedAt - a.stateChangedAt;
}
